import { useEffect, useRef, useState } from "react";

/** Reveals text word-by-word for a lightweight streaming feel (no SSE required). */
export function useStreamText(
  fullText: string,
  enabled: boolean,
  onComplete?: () => void,
  msPerWord = 28,
) {
  const [displayed, setDisplayed] = useState(enabled ? "" : fullText);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled) {
      setDisplayed(fullText);
      return;
    }

    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayed(fullText);
      onCompleteRef.current?.();
      return;
    }

    const tokens = fullText.match(/\S+\s*/g) ?? (fullText ? [fullText] : []);
    if (tokens.length === 0) {
      setDisplayed("");
      onCompleteRef.current?.();
      return;
    }

    setDisplayed("");
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setDisplayed(tokens.slice(0, index).join(""));
      if (index >= tokens.length) {
        window.clearInterval(timer);
        onCompleteRef.current?.();
      }
    }, msPerWord);

    return () => window.clearInterval(timer);
  }, [fullText, enabled, msPerWord]);

  return displayed;
}
